/**
 * Seasonal Tab Component
 * Features: CSV/PDF export, configurable row count, contextual empty state
 */

import { useState } from "react";
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, Legend,
} from "recharts";
import { Calendar } from "lucide-react";
import { 
  ChartCard, TableCard, CustomTooltip, DataTable, 
  TableRow, TableCell, EmptyState, EMPTY_STATES 
} from "./shared";
import {
  CHART_COLORS, CATEGORY_COLORS, CHART_STYLE, formatCurrency, formatCompactCurrency,
  formatMonthLabel, exportToCSV, DEFAULT_ROW_COUNT,
  generateAnalyticsPdfContent, generateTableHtml,
} from "../../lib/chartUtils";
import useExportDialog from "../../hooks/useExportDialog";

const SeasonalTab = ({ data }) => {
  const [rowCount, setRowCount] = useState(DEFAULT_ROW_COUNT);
  const { exportToPdf } = useExportDialog();

  if (!data) {
    return (
      <EmptyState 
        icon={Calendar}
        {...EMPTY_STATES.seasonal}
      />
    );
  }

  const { monthly = [], quarterly = [] } = data;

  if (monthly.length === 0 && quarterly.length === 0) {
    return (
      <EmptyState 
        icon={Calendar}
        {...EMPTY_STATES.seasonal}
      />
    );
  }

  const handleExportCSV = () => {
    exportToCSV(monthly, "seasonal-trends",
      ["Month", "Jobs Started", "Jobs Completed", "Hours", "Revenue"],
      {
        "Month": "month",
        "Jobs Started": "jobs_started",
        "Jobs Completed": "jobs_completed",
        "Hours": "total_hours",
        "Revenue": "revenue",
      }
    );
  };

  const handleExportPDF = () => {
    const monthlyRows = monthly.slice(0, rowCount).map(row => [
      formatMonthLabel(row.month),
      row.jobs_started,
      row.jobs_completed,
      `${row.total_hours}h`,
      formatCurrency(row.revenue),
    ]);

    const monthlyTableHtml = generateTableHtml(
      ["Month", "Jobs Started", "Jobs Completed", "Hours", "Revenue"],
      monthlyRows,
      ["left", "right", "right", "right", "right"]
    );

    const quarterlyRows = quarterly.map(row => [
      row.quarter,
      formatCurrency(row.revenue),
    ]);

    const quarterlyTableHtml = quarterly.length > 0 ? generateTableHtml(
      ["Quarter", "Revenue"],
      quarterlyRows,
      ["left", "right"]
    ) : "";

    const totals = monthly.reduce((acc, row) => ({
      started: acc.started + row.jobs_started,
      completed: acc.completed + row.jobs_completed,
      hours: acc.hours + row.total_hours,
      revenue: acc.revenue + row.revenue,
    }), { started: 0, completed: 0, hours: 0, revenue: 0 });

    const summaryHtml = `
      <div style="display: flex; flex-wrap: wrap; gap: 24px; margin-bottom: 16px;">
        <div><strong>Jobs Started:</strong> ${totals.started}</div>
        <div><strong>Jobs Completed:</strong> ${totals.completed}</div>
        <div><strong>Total Hours:</strong> ${totals.hours}h</div>
        <div><strong>Total Revenue:</strong> ${formatCurrency(totals.revenue)}</div>
      </div>
    `;

    const sections = [
      { title: "Summary", content: summaryHtml },
      { title: "Monthly Trends", content: monthlyTableHtml },
    ];

    if (quarterlyTableHtml) {
      sections.push({ title: "Quarterly Revenue", content: quarterlyTableHtml });
    }

    const content = generateAnalyticsPdfContent("Seasonal Trends Report", sections);
    exportToPdf("Seasonal Trends Report", content);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Job Volume */}
        <ChartCard title="Monthly Job Activity">
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={monthly} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
              <CartesianGrid {...CHART_STYLE.grid} />
              <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={CHART_STYLE.axis} />
              <YAxis tick={CHART_STYLE.axis} />
              <Tooltip content={<CustomTooltip />} labelFormatter={formatMonthLabel} />
              <Legend />
              <Area type="monotone" dataKey="jobs_started" name="Jobs Started" stroke={CHART_COLORS.success} fill={CHART_COLORS.success} fillOpacity={0.3} />
              <Area type="monotone" dataKey="jobs_completed" name="Jobs Completed" stroke={CHART_COLORS.info} fill={CHART_COLORS.info} fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Quarterly Revenue */}
        {quarterly.length > 0 && (
          <ChartCard title="Quarterly Revenue">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={quarterly} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
                <CartesianGrid {...CHART_STYLE.grid} />
                <XAxis dataKey="quarter" tick={CHART_STYLE.axis} />
                <YAxis tickFormatter={formatCompactCurrency} tick={CHART_STYLE.axis} />
                <Tooltip content={<CustomTooltip formatter={formatCurrency} />} />
                <Bar dataKey="revenue" name="Revenue" fill={CHART_COLORS.purple} radius={[4, 4, 0, 0]}>
                  {quarterly.map((entry, index) => (
                    <Cell key={entry.quarter} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
      </div>

      {/* Monthly Hours Trend */}
      <ChartCard title="Monthly Labor Hours" className="">
        <ResponsiveContainer width="100%" height={256}>
          <LineChart data={monthly} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
            <CartesianGrid {...CHART_STYLE.grid} />
            <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={CHART_STYLE.axis} />
            <YAxis tickFormatter={(v) => `${v}h`} tick={CHART_STYLE.axis} />
            <Tooltip content={<CustomTooltip formatter={(v) => `${v} hours`} />} labelFormatter={formatMonthLabel} />
            <Line type="monotone" dataKey="total_hours" name="Hours" stroke="#14b8a6" strokeWidth={2} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Revenue by Month */}
      <ChartCard title="Monthly Revenue (Contract Value)" className="">
        <ResponsiveContainer width="100%" height={256}>
          <BarChart data={monthly} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
            <CartesianGrid {...CHART_STYLE.grid} />
            <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={CHART_STYLE.axis} />
            <YAxis tickFormatter={formatCompactCurrency} tick={CHART_STYLE.axis} />
            <Tooltip content={<CustomTooltip formatter={formatCurrency} />} labelFormatter={formatMonthLabel} />
            <Bar dataKey="revenue" name="Revenue" fill={CHART_COLORS.success} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Monthly Details Table */}
      <TableCard 
        title="Monthly Trend Details"
        onExportCSV={handleExportCSV}
        onExportPDF={handleExportPDF}
        rowCount={rowCount}
        onRowCountChange={setRowCount}
      >
        <DataTable headers={[
          { label: "Month" },
          { label: "Jobs Started", align: "right" },
          { label: "Jobs Completed", align: "right" },
          { label: "Hours", align: "right" },
          { label: "Revenue", align: "right" },
        ]}>
          {monthly.slice(0, rowCount).map((row) => (
            <TableRow key={row.month} className="table-row">
              <TableCell className="font-medium">{formatMonthLabel(row.month)}</TableCell>
              <TableCell className="text-right">{row.jobs_started}</TableCell>
              <TableCell className="text-right">{row.jobs_completed}</TableCell>
              <TableCell className="text-right">{row.total_hours}h</TableCell>
              <TableCell className="text-right">{formatCurrency(row.revenue)}</TableCell>
            </TableRow>
          ))}
        </DataTable>
      </TableCard>
    </div>
  );
};

export default SeasonalTab;
